import {
    AppBar as MuiAppBar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Toolbar,
    Divider,
} from "@mui/material";
import { CheckCircle, Menu, Warning } from "@mui/icons-material";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import Authentication from "../modules/authentication";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { PATH_ROOT } from "../routes";
import { getWebsiteStage, STAGE_DEV, STAGE_PROD, STAGE_RC } from "../modules/stages";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { wait } from "../utils/hooks";
import { ReactComponent as IRISLogo } from "../res/images/iris_logo.svg";
import { countProcessProblems } from "./ProcessStatusDrawer";

const gApiClient = Authentication.getAPIClient();

/**
 * Chip summarizing the process statuses - toggles the process statuses drawer when clicked
 * @param currentProcesses all process statuses returned from the backend
 * @param onClick click handler
 */
function ProcessStatusChip({ currentProcesses, onClick }) {
    const problemCount = countProcessProblems(currentProcesses);

    let label, icon, color;
    if (currentProcesses.length === 0) {
        label = "Processes: Loading...";
        icon = <HourglassTopIcon />;
        color = "info";
    } else if (problemCount > 0) {
        label = `Processes: ${problemCount} issue${problemCount > 1 ? "s" : ""}`;
        icon = <Warning />;
        color = "error";
    } else {
        label = "Processes: All OK";
        icon = <CheckCircle />;
        color = "success";
    }

    // White background, so it's visible on any app bar color (including the red dev one)
    return (
        <Chip
            label={label}
            icon={icon}
            onClick={onClick}
            color={color}
        />
    );
}

/**
 * Top app bar
 * @param onToggleSideMenu called when the side menu toggle button is clicked
 * @param onToggleProcessDrawer called when the process statuses toggle button is clicked
 */
export default function AppBar({ onToggleSideMenu, onToggleProcessDrawer }) {
    const loginState = useSelector((state) => state.user.loginState);
    const userDetails = useSelector((state) => state.user.userDetails);
    const currentProcesses = useSelector(
        (state) => state.indexes.currentProcesses,
    );

    const websiteStage = getWebsiteStage();
    const loggedIn =
        loginState !== Authentication.LOGIN_STATUS_NOT_LOGGED_IN &&
        userDetails;
    const [showResetDBDialog, setShowResetDBDialog] = useState(false);
    const [resettingDB, setResettingDB] = useState(false);

    const resetDB = async () => {
        setResettingDB(true);

        // Reset DB using API call
        let response = await gApiClient.callApi("reset-db-from-prod", {
            method: "POST",
        });
        const backgroundFunctionId = response.data.background_function_id;
        let backgroundFunction;

        do {
            response = await gApiClient.callApi("background_function", {
                method: "GET",
                params: {
                    background_function_id: backgroundFunctionId,
                },
            });
            backgroundFunction = response.data;

            await wait(500);
        } while (
            !backgroundFunction ||
            backgroundFunction.status !== "finished"
        );

        // Force a refresh of the website
        window.location.reload();
    };

    return (
        <MuiAppBar
            position="fixed"
            color={(websiteStage === STAGE_DEV) ? "error" : (((websiteStage === STAGE_RC) ? "warning" : "primary"))}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
        >
            <Toolbar>
                {loggedIn && (
                    <IconButton
                        color="inherit"
                        edge="start"
                        onClick={onToggleSideMenu}
                        sx={{ mr: 2 }}
                    >
                        <Menu />
                    </IconButton>
                )}
                <Link to={PATH_ROOT} className="flex">
                    <IRISLogo className="w-[100px]" />
                </Link>
                {websiteStage !== STAGE_PROD && (
                    <>
                        <Divider orientation="vertical" flexItem variant="middle" sx={{ ml: 1.5, borderColor: "currentColor" }} />
                        <Chip
                            label={websiteStage.toUpperCase()}
                            size="medium"
                            variant="outlined"
                            sx={{
                                ml: 1.5,
                                color: "inherit",
                                borderColor: "currentColor",
                                fontWeight: "bold",
                            }}
                        />
                    </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {loggedIn && (
                    <>
                        {websiteStage !== STAGE_PROD && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setShowResetDBDialog(true)}
                                sx={{
                                    mr: 2,
                                    height: 40,
                                    width: 150,
                                }}
                            >
                                Reset DB
                            </Button>
                        )}
                        <ProcessStatusChip
                            currentProcesses={currentProcesses}
                            onClick={onToggleProcessDrawer}
                        />
                    </>
                )}
            </Toolbar>

            <Dialog
                open={showResetDBDialog}
                onClose={() => {
                    // Prevent from canceling the dialog by clicking on the backdrop
                    return;
                }}
                maxWidth={"sm"}
                fullWidth={true}
            >
                <DialogTitle>
                    Reset <u>{getWebsiteStage()}</u> DB (from Production)
                </DialogTitle>
                <DialogContent>
                    <div>
                        Are you sure you want to reset DB for this stage (
                        <b>{getWebsiteStage()}</b>) from Production DB? All DB
                        tables in this staging environment will be cleared out,
                        and copied over from Production DB.
                        <br />
                        <br />
                        <b>Note:</b> Website will be reloaded once resetting is
                        done.
                    </div>
                </DialogContent>
                <DialogActions>
                    <LoadingButton onClick={resetDB} loading={resettingDB}>
                        Yes
                    </LoadingButton>
                    <Button
                        onClick={() => setShowResetDBDialog(false)}
                        disabled={resettingDB}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </MuiAppBar>
    );
}
